import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useCallback } from 'react';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import DeviceTile, { DeviceTileSkeleton } from './DeviceTile';
import { useUnLinkDeviceMutation } from '../../../../store/api-slice';

interface Props {
  gateways: MedisanteDevice[];
  sensors: MedisanteDevice[];
  errorMessage?: string;
  isLoading: boolean;
  open: boolean;
  onClose: (unlinkAll?: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  tile: {
    marginBottom: theme.spacing(1),
  },
  question: {
    marginBottom: theme.spacing(2),
  },
}));

function UnlinkAll(props: Props) {
  const { open, onClose, gateways, sensors, isLoading, errorMessage } = props;
  const config = useConfig();
  const classes = useStyles();

  const [unlinkDevices, { error, ...unlinkingStatus }] =
    useUnLinkDeviceMutation();

  const unlinkAll = useCallback(async () => {
    const promises = gateways
      .filter((g) => sensors.some((s) => s.gatewayID === g.id))
      .map((gw) => {
        const btdevices = sensors.filter((d) => d.gatewayID === gw.id);
        return unlinkDevices({
          gatewayId: gw.id,
          deviceIds: btdevices,
          apiBaseURL: config.apiBaseURL,
        });
      });
    await Promise.all(promises);
    onClose(true);
  }, [sensors, gateways, unlinkDevices, onClose, config.apiBaseURL]);

  const errMessage = errorMessage || error || '';

  return (
    <Dialog open={open} onClose={() => onClose()}>
      <DialogTitle>Unlink all</DialogTitle>
      <DialogContent>
        <Typography className={classes.question}>
          {sensors.length > 0
            ? 'Are you sure you want to unlink all selected devices from their gateway?'
            : 'There is no linked sensors.'}
        </Typography>
        {sensors.map((device) => (
          <Grid
            key={device.id}
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item sm={5}>
              <DeviceTile device={device} className={classes.tile} />
            </Grid>
            <Grid item sm={2}>
              <Typography align="center">linked to</Typography>
            </Grid>
            <Grid item sm={5}>
              {!isLoading ? (
                <DeviceTile
                  device={gateways.find((g) => g.id === device.gatewayID)!}
                  className={classes.tile}
                />
              ) : (
                <DeviceTileSkeleton />
              )}
            </Grid>
          </Grid>
        ))}
        {errMessage ? (
          <Alert variant="standard" severity="error">
            {errMessage}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <ButtonProgress
          disabled={sensors.length === 0}
          isLoading={isLoading || unlinkingStatus.isLoading}
          variant="contained"
          color="primary"
          onClick={unlinkAll}
        >
          Confirm
        </ButtonProgress>
      </DialogActions>
    </Dialog>
  );
}

export default UnlinkAll;
